import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import axios from 'axios';
import _camelCase from 'lodash/camelCase';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import _set from 'lodash/set';
import _size from 'lodash/size';
import _split from 'lodash/split';
import _startsWith from 'lodash/startsWith';
import {
  CONFLICT,
  FORBIDDEN,
  INTERNAL_SERVER_ERROR,
  METHOD_NOT_ALLOWED,
  NOT_FOUND,
  UNAUTHORIZED,
} from 'http-status-codes';
import PropTypes from 'prop-types';
// Custom
import { logout, hideLoading } from 'actions';
import { dialogShape } from 'constants/PropTypesShapes';
import withMessageDialog from 'hocs/WithMessageDialog';

class ResponseInterceptor extends React.Component {
  constructor(props) {
    super(props);

    this.interceptor = axios.interceptors.response.use(
      (response) => {
        let errorDesc;
        // With GraphQL API, server returns a HTTP 200 even when an error occured
        // Check errors here
        const { errors } = _get(response, 'data');
        if (errors) {
          // Errors is an array of objects {locations, message} with at least one element
          // Analyse the first one which could be a generic error
          const error = errors[0];
          const message = error && _camelCase(error.message);
          // console.log("MSG", message);
          if (message === 'unauthorized') {
            errorDesc = 'unauthorized';
          } else if (message === 'forbidden') {
            errorDesc = 'forbidden';
          } else if (
            _includes(message, 'NotFound')
            && !_includes(error.path, 'createPasswordRecoveryLink')
            && !_includes(error.path, 'searchOnDemandAccess')
            && !_includes(error.path, 'getOnDemandAccess')
            && !_includes(error.path, 'getCarparkBySlug')
            && !_includes(error.path, 'getOffer')
            && !_includes(error.path, 'getSlugByDomain')
            && !_includes(error.path, 'getMyBooking')
            && !_includes(error.path, 'getUpdatedMyBooking')
          ) {
            errorDesc = 'notFound';
          } else if (
            _startsWith(message, 'cannotQueryField')
            || _startsWith(message, 'argumentInputHasInvalidValue')
            || _startsWith(message, 'unknownType')
            || _startsWith(message, 'unknownArgument')
            || _startsWith(message, 'unknownFragment')
            || _startsWith(message, 'syntaxError')
          ) {
            // Normally these errors must not happen
            // They correspond with bad use of GraphQL API
            this.doTreatError('unknown');
            // Return response with modified errors to avoid bad display in initial view
            return {
              ...response,
              data: {
                ...response.data,
                errors: [{ message: 'unknown', path: [] }],
              },
            };
          }

          // Treat error
          if (errorDesc) {
            this.doTreatError(errorDesc);
          }
        }
        return response;
      },
      (error) => {
        // The error to be forwarded (may be updated)
        const forwardedError = { ...error };
        // Error desc to be set in case of generic message display need
        let errorDesc;
        const { request, response } = error;
        if (response) {
          // Response errors
          const { status, data, config } = response;
          const { responseURL } = request;
          const errors = data && _camelCase(data.errors);

          // Case of error sent by server
          if (status === UNAUTHORIZED) {
            errorDesc = 'unauthorized';
          } else if (status === FORBIDDEN && errors === 'forbidden') {
            errorDesc = 'forbidden';
          } else if (status === NOT_FOUND && !_includes(responseURL, 'recover_password')) {
            errorDesc = 'notFound';
          } else if (status === CONFLICT) {
            // Conflict can be either anOperationIsCurrentlyInProgress or allDevicesDoNotSupportTheSameProtocolVersion error
            // Display error according to `errors` value sent by server
            errorDesc = errors;
          } else if (status === METHOD_NOT_ALLOWED) {
            // Concerns not allowed operation on put/delete resource
            // Get the type of resource concerned
            const urlSplit = _split(config.url, '/');
            const resource = urlSplit[_size(urlSplit) - 2];
            // No generic message here, but set a data errors desc to be parsed by initial component
            _set(forwardedError, 'response.data.errors', `Method not allowed on ${config.method} ${resource}`);
          } else if (status === INTERNAL_SERVER_ERROR) {
            errorDesc = 'internalServer';
          }
        } else if (request) {
          // Request errors
          const { status } = request;
          if (status === 0) {
            // '0' is set by browser by default, in this case the request failed
            errorDesc = 'network';
          } else {
            // Normally, we can't be in this case
            errorDesc = 'unknown';
          }
        } else {
          // Other errors
          errorDesc = 'unknown';
        }

        // Treat error
        if (errorDesc) {
          this.doTreatError(errorDesc);
        }
        return Promise.reject(forwardedError);
      },
    );
  }

  componentWillUnmount() {
    axios.interceptors.response.eject(this.interceptor);
  }

  // Private
  doTreatError(errorDesc) {
    const {
      loggedIn, dialogsList, handleDisplayMessage, handleLogout, handleHideLoading,
    } = this.props;
    let displayError = true;
    let performLogout = false;
    if (errorDesc === 'unauthorized') {
      if (loggedIn) {
        performLogout = true;
        displayError = false;
      } else {
        displayError = false;
      }
    } else if (errorDesc === 'forbidden') {
      performLogout = true; // TODO: do not logout on forbidden, to be treated in each component
    }

    if (displayError) {
      // Display error message if not already displayed
      const messageId = `error.${errorDesc}`;
      if (!_find(dialogsList, (dialog) => _get(dialog, 'props.component.props.message.id') === messageId)) {
        handleDisplayMessage(messageId, {}, 'danger', performLogout);
        // Hide eventual loading
        handleHideLoading();
      }
    }
    if (performLogout) {
      handleLogout();
    }
  }

  render() {
    return <div />;
  }
}

ResponseInterceptor.propTypes = {
  loggedIn: PropTypes.bool,
  dialogsList: PropTypes.arrayOf(dialogShape).isRequired,
  handleDisplayMessage: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  handleHideLoading: PropTypes.func.isRequired,
};

ResponseInterceptor.defaultProps = {
  loggedIn: false,
};

const mapStateToProps = (state) => ({
  loggedIn: state.auth.loggedIn,
  dialogsList: state.dialog.list,
});

const mapDispatchToProps = (dispatch) => ({
  handleLogout: () => {
    dispatch(logout());
  },
  handleHideLoading: () => {
    dispatch(hideLoading());
  },
});

module.exports = compose(connect(mapStateToProps, mapDispatchToProps), withMessageDialog)(ResponseInterceptor);



// WEBPACK FOOTER //
// ./containers/ResponseInterceptor.js